import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faClosedCaptioning, faUpload, faVideo } from '@fortawesome/free-solid-svg-icons';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import UploadSection from './components/UploadSection';
import UploadProgressSection from './components/UploadProgressSection';
import ProjectsList from './components/ProjectsList';
import Dashboard from '../Dashboard/Dashboard';
import { useUploadLogic } from './hooks/useUploadLogic';
import { fetchProjects, Project, ProjectStage, deleteProject, duplicateProject } from '../../services/projectService';
import styles from './CaptionLandingPageModern.module.css';

const CaptionLandingPage: React.FC = () => {
  const { t } = useTranslation('captionLandingPage');
  
  // URL and project states
  const [videoUrl, setVideoUrl] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState<'all' | 'recent'>('all');
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [jobId, setJobId] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  
  // File input ref for upload
  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  
  // Upload logic from custom hook
  const {
    isLoading,
    error,
    isChunkedUpload,
    uploadProgress,
    uploadStats,
    retryAttempts,
    fileWarning,
    startUpload,
    setError,
    setFileWarning
  } = useUploadLogic();

  // Load projects on component mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setIsProjectsLoading(true);
        const response = await fetchProjects(1, 1000);
        
        if (response && 'projects' in response) {
          // Filter projects to include those with video URLs (original or generated)
          const captionProjects = response.projects.filter(p => 
            p.originalVideoUrl || p.generatedVideoUrl
          );
          setProjects(captionProjects);
        } else {
          // Demo projects if API fails
          setProjects([
            {
              id: "project-1",
              title: "Welcome Message Video",
              type: "video",
              date: new Date().toISOString(),
              createdAt: new Date().toISOString(),
              duration: "2:45",
              originalVideoUrl: "https://example.com/video1.mp4",
              thumbnail: "https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg",
              captionedVideoUrl: "https://example.com/captioned-video1.mp4",
              stage: ProjectStage.CAPTION
            }
          ]);
        }
        setIsProjectsLoading(false);
      } catch (err) {
        console.error('Error loading projects:', err);
        setIsProjectsLoading(false);
      }
    };
    loadProjects();
  }, []);  // Handlers
  const handleUrlSubmit = async () => {
    if (!videoUrl) {
      alert('Please enter a valid URL');
      return;
    }
    
    try {
      const queryParams = new URLSearchParams({ 'video-url': videoUrl });
      navigate(`/caption?${queryParams.toString()}`);
    } catch (error) {
      console.error("URL processing failed:", error);
      alert('There was an error processing your video URL');
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🎯 File change handler triggered');
    
    if (!event.target.files || !event.target.files[0]) {
      console.log('❌ No file selected');
      return;
    }
    
    const file = event.target.files[0];
    console.log(`✅ File selected: ${file.name}, Size: ${(file.size / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`📋 File type: ${file.type}`);
    
    // 5GB file size validation
    const maxSizeInBytes = 5 * 1024 * 1024 * 1024; // 5GB in bytes
    if (file.size > maxSizeInBytes) {
      const fileSizeInGB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
      setError(t('errors.fileTooLarge', { size: fileSizeInGB }));
      // Clear the file input
      event.target.value = '';
      return;
    }
    
    try {
      console.log('🚀 Calling startUpload...');
      const result = await startUpload(file);
      console.log('📦 Upload result:', result);
      
      if (result.jobId) {
        console.log(`🆔 Setting job ID: ${result.jobId}`);
        setJobId(result.jobId);
      }
    } catch (error) {
      console.error('💥 Upload failed:', error);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const processData = (videoUrl: string, projectId: string) => {
    setUploadStatus(t('status.videoLoaded'));
    setIsProcessing(false); // Clear processing state when complete
    const queryParams = new URLSearchParams({ 'video-url': videoUrl, 'project-id': projectId });
    navigate(`/caption?${queryParams.toString()}`);
  };

  // Project handlers
  const handleContinueProject = async (project: Project) => {
    const queryParams = new URLSearchParams();
    queryParams.append('project-id', project.id);
    
    // Prioritize generatedVideoUrl (from content creation workflow), then fallback to originalVideoUrl
    // Skip captionedVideoUrl since it already has captions
    if (project.generatedVideoUrl) {
      queryParams.append('video-url', project.generatedVideoUrl);
    } else if (project.originalVideoUrl) {
      queryParams.append('video-url', project.originalVideoUrl);
    }
    
    navigate(`/caption?${queryParams.toString()}`);
  };

  const handleDeleteProject = async (projectId: string) => {
    // Show confirmation dialog
    const projectToDelete = projects.find(p => p.id === projectId);
    const projectTitle = projectToDelete?.title || 'this project';
    
    if (!window.confirm(t('confirmations.deleteProject', { title: projectTitle }))) {
      return;
    }

    try {
      const success = await deleteProject(projectId);
      if (success) {
        // Only remove from frontend state if backend deletion succeeded
        setProjects(projects.filter(project => project.id !== projectId));
        setActiveActionDropdown(null); // Close dropdown
        console.log(`✅ Successfully deleted project: ${projectId}`);
      } else {
        console.error(`❌ Failed to delete project: ${projectId}`);
        alert('Failed to delete project. Please try again.');
      }
    } catch (error) {
      console.error(`❌ Error deleting project ${projectId}:`, error);
      alert(t('errors.deleteProject'));
    }
  };

  const handleDuplicateProject = async (projectId: string) => {
    try {
      const duplicatedProject = await duplicateProject(projectId);
      if (duplicatedProject) {
        // Add the new project to the beginning of the list
        setProjects([duplicatedProject, ...projects]);
        setActiveActionDropdown(null); // Close dropdown
        console.log(`✅ Successfully duplicated project: ${projectId}`);
      } else {
        console.error(`❌ Failed to duplicate project: ${projectId}`);
        alert('Failed to duplicate project. Please try again.');
      }
    } catch (error) {
      console.error(`❌ Error duplicating project ${projectId}:`, error);
      alert(t('errors.duplicateProject'));
    }
  };

  const toggleActionDropdown = (projectId: string) => {
    setActiveActionDropdown(activeActionDropdown === projectId ? null : projectId);
  };
  return (
    <div className={styles.container}>
      <SEO
        title={seoConfig.captionGenerator.title}
        description={seoConfig.captionGenerator.description}
        keywords={seoConfig.captionGenerator.keywords}
        url="https://kingdomstudio.pro/Caption-LandingPage"
      />
      <Navigation />
      <main className={styles.main}>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <h1 className={styles.title}>
            <FontAwesomeIcon icon={faClosedCaptioning} className={styles.titleIcon} />
            {t('hero.title')}
          </h1>
          <p className={styles.subtitle}>
            {t('hero.subtitle')}
          </p>
          
          {/* Action Buttons */}
          <div className={styles.headerActions}>
            <Button
              className={styles.createButton}
              onClick={handleUploadClick}
              leftSection={<FontAwesomeIcon icon={faPlus} />}
              size="lg"
              loading={isLoading || isProcessing}
              disabled={isLoading || isChunkedUpload || isProcessing}
            >
              {isLoading ? 
                t('hero.processing') : 
                isProcessing ? 
                  t('hero.processingVideo') : 
                  t('hero.uploadButton')
              }
            </Button>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          accept="video/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        
        {jobId && (
          <div className={styles.dashboardContainer}>
            <Dashboard 
              jobId={jobId} 
              callBackFunc={processData} 
              onProcessingStateChange={setIsProcessing}
            />
          </div>
        )}
        
        {error && (
          <div className={styles.errorContainer}>
            <h3>{t('errors.uploadFailed')}</h3>
            <p>{error}</p>
            <button 
              onClick={() => setError(null)}
              className={styles.errorButton}
            >
              {t('errors.tryAgain')}
            </button>
          </div>
        )}
        
        <ProjectsList
          projects={projects}
          isLoading={isProjectsLoading}
          error={error}
          searchTerm={searchTerm}
          selectedOption={selectedOption}
          activeActionDropdown={activeActionDropdown}
          onSearchChange={setSearchTerm}
          onFilterChange={setSelectedOption}
          onToggleDropdown={toggleActionDropdown}
          onContinueProject={handleContinueProject}
          onDeleteProject={handleDeleteProject}
          onDuplicateProject={handleDuplicateProject}
          onUploadClick={handleUploadClick}
        />
      </main>
    </div>
  );
};

export default CaptionLandingPage;
